import { promoCodeSchema } from 'src/common-services/types/signup-types'
import { z } from 'zod'

export enum Role {
  user = 'USER',
  admin = 'ADMIN',
}

const userPlanSchema = z.enum(['none', 'mobile', 'standard'])
export type UserPlan = z.infer<typeof userPlanSchema>

const userPaymentMethodSchema = z.enum(['iap', 'direct'])
export type UserPaymentMethod = z.infer<typeof userPaymentMethodSchema>

const userInfoSchema = z.object({
  email: z.string().email(),
  role: z.nativeEnum(Role),
  enabled: z.boolean(),
})

export const userSchema = userInfoSchema.extend({
  id: z.string().uuid(),
  created: z.date().or(z.string()),
  lastLogin: z.date().or(z.string()).nullable(),
  company: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      logoUrl: z.string().nullable(),
    })
    .optional(),
  plan: userPlanSchema,
  paymentMethod: userPaymentMethodSchema,
})

export type UserInfo = z.infer<typeof userInfoSchema>

// this is considered the 'mutable' portion of a user
// and can be updated via the api
export type User = z.infer<typeof userSchema>

export const USER_TIERS = ['free', 'entry', 'intermediate', 'full', 'premium', 'trial'] as const
// For self-serve these tiers allow access to the app
export const ALLOWED_TIERS = ['trial', 'full', 'premium']
export const userTierSchema = z.enum(USER_TIERS)
export type UserTier = z.infer<typeof userTierSchema>

const userSignupMethodSchema = z.enum([
  'desktop.email',
  'desktop.google',
  'mobile.email',
  'mobile.google',
  'mobile.facebook',
  'mobile.apple',
  'admin',
])
export type UserSignupMethod = z.infer<typeof userSignupMethodSchema>

// base system metadata for all users
const userSystemMetaBaseSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  tier: userTierSchema,
  expires: z.date().or(z.string()),
  signupMethod: userSignupMethodSchema.optional(),
  phone: z.string().optional(),
})
export type UserSystemMetaBase = z.infer<typeof userSystemMetaBaseSchema>

export const allUserMetaSchema = userSystemMetaBaseSchema
export type AllUserMeta = z.infer<typeof allUserMetaSchema>

// Merge user + all metadata types into one
export const userWithMetaSchema = userSchema.merge(allUserMetaSchema)
export type UserWithMeta = z.infer<typeof userWithMetaSchema>

export type UserCompany = {
  userId: User['id']
  companyId: string
}

export type Identity = { id: User['id']; email: string }
export type Access = { id: User['id']; enabled: boolean; role: Role; tier: UserTier; impersonated?: boolean }

export const loginRequestSchema = z.object({
  email: userSchema.shape.email,
  // https://github.com/kelektiv/node.bcrypt.js#security-issues-and-concerns
  password: z.string().min(6).max(50),
})
export type LogInRequest = z.infer<typeof loginRequestSchema>

export const loginProviders = ['apple', 'google', 'facebook'] as const
const loginProviderSchema = z.enum(loginProviders)
export type LoginProvider = z.infer<typeof loginProviderSchema>

export const providerUserSchema = z.object({
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  contactEmail: z.string(),
  provider: loginProviderSchema,
})
export type ProviderUser = z.infer<typeof providerUserSchema>

export const loginProviderRequestSchema = z.object({
  token: z.string(),
  provider: loginProviderSchema,
})
export type LoginProviderRequest = z.infer<typeof loginProviderRequestSchema>

export const signInWithGoogleRequestSchema = z.object({
  accessToken: z.string(),
  code: z.optional(z.literal(promoCodeSchema.Values.BRKR)),
})
export type SignInWithGoogleRequest = z.infer<typeof signInWithGoogleRequestSchema>

export const updateUserRequestSchema = userInfoSchema
  .extend({
    id: userSchema.shape.id,
    plan: userSchema.shape.plan,
    paymentMethod: userSchema.shape.paymentMethod,
  })
  .merge(allUserMetaSchema)
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>

export const createUserRequestSchema = userInfoSchema
  .extend({
    password: loginRequestSchema.shape.password,
    plan: userSchema.shape.plan,
    paymentMethod: userSchema.shape.paymentMethod,
  })
  .merge(allUserMetaSchema)

export type CreateUserRequest = z.infer<typeof createUserRequestSchema>
export const createFreemiumUserRequestSchema = createUserRequestSchema.pick({
  email: true,
  firstName: true,
  lastName: true,
  password: true,
  signupMethod: true,
  expires: true,
})
export type CreateFreemiumUserRequest = z.infer<typeof createFreemiumUserRequestSchema>
export const changePasswordRequestSchema = z.object({
  email: userSchema.shape.email,
  oldPassword: loginRequestSchema.shape.password,
  newPassword: loginRequestSchema.shape.password,
})

export const updateProfileRequestSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string().optional(),
})
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>

export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>
export type GetUsersRequest = { offset?: number; limit?: number }
export type GetUserRequest = { id: User['id'] }
export type GetUserFromEmailRequest = { email: string }
export type ForgotPasswordRequest = { email: string }
export type RedeemResetTokenRequest = { email: string; password: string }

export type LogInResponse = { access: string; identity: string }
export type UpdateUserResponse = UserWithMeta
export type CreateUserResponse = UserWithMeta
export type ChangePasswordResponse = UserWithMeta
export type GetUsersResponse = { users: UserWithMeta[]; total: number }
export type GetUserResponse = UserWithMeta

export type UserMetaDataResponse = {
  type: UserMetaDataType
  data: UserSystemMetaBase
}

export enum UserMetaDataType {
  SYSTEM = 'system',
}

export type PasswordResetToken = {
  id: string
  token: string
  reedemed?: string
  created: string
}
